import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { API_URL } from "../../../config";

const ResendConfirmationModal = ({
  open,
  onClose,
  orderId,
  onResendSuccess,
}) => {
  const [messageLogs, setMessageLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      fetchMessageLogs();
    }
  }, [open, orderId]);

  const fetchMessageLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        API_URL + `/orders/${orderId}/message-logs`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        const resp = await response.json();
        setMessageLogs(resp.data || []);
      } else if (response.status === 401) {
        setError("Unauthorized access");
      } else {
        setError("Failed to fetch message logs");
      }
    } catch (err) {
      setError("Error fetching message logs: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResending(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(
        API_URL + `/orders/resend-confirmation-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ order_id: orderId }),
        }
      );

      if (response.status === 200) {
        const resp = await response.json();
        setSuccess("Confirmation email sent successfully!");
        setShowConfirmDialog(false);
        // Refresh message logs
        setTimeout(() => {
          fetchMessageLogs();
          if (onResendSuccess) onResendSuccess();
        }, 1000);
      } else if (response.status === 401) {
        setError("Unauthorized access");
      } else {
        const resp = await response.json();
        setError(resp.message || "Failed to resend confirmation email");
      }
    } catch (err) {
      setError("Error resending confirmation email: " + err.message);
    } finally {
      setResending(false);
    }
  };

  const handleClose = () => {
    setError("");
    setSuccess("");
    setShowConfirmDialog(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="resend-confirmation-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Resend Confirmation Email
        </DialogTitle>
        <DialogContent sx={{ minHeight: "300px" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Message Logs
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : messageLogs.length > 0 ? (
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Message ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Triggered By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messageLogs.map((log, index) => (
                    <TableRow key={log.id || index}>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {log.messageId || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {log.triggeredBy || "N/A"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        <span
                          style={{
                            color: log.status === "1" ? "green" : "orange",
                            fontWeight: 500,
                          }}
                        >
                          {log.status === "1" ? "Sent" : "Pending"}
                        </span>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>
                        {new Date(log.created).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: "#666", py: 2 }}>
              No message logs available
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => setShowConfirmDialog(true)}
           
            disabled={resending}
            sx={{
              backgroundColor: "#9c27b0",
              color: "white!important",
              "&:hover": {
                backgroundColor: "#7b1fa2",
                color: "white",
              },
            }}
          >
            {resending ? "Sending..." : "Resend Confirmation Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="confirm-resend-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Resend
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to resend the confirmation email for Order BP{orderId}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleResendConfirmation}
            variant="contained"
            disabled={resending}
            sx={{
              backgroundColor: "#9c27b0",
             color: "white!important",
              "&:hover": {
                backgroundColor: "#7b1fa2",
              },
            }}
          >
            {resending ? "Sending..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResendConfirmationModal;
